import apiPlugin from "./apiPlugin";
import { ApiResponse } from "./../common/interfaces";
import {
	FetchBatchMessageDetailModel,
	PreSubmissionConfigurationModel,
} from "../models/BatchMessage/BatchMessageDetailModel";
import BatchMessageDraftModel from "../models/BatchMessage/BatchMessageDraftModel";
import BatchMessageDetailModel from "../models/BatchMessage/BatchMessageDetailModel";
import BatchMessageLanguageDetailModel, {
	LanguageModel,
} from "../models/BatchMessage/BatchMessageLanguageDetailModel";
import Shared from "../common/shared";
import BatchMessageGroupDetailModel from "../models/BatchMessage/BatchMessageGroupDetailModel";
import PhoneNumbers from "../models/BatchMessage/PhoneNumbersModel";
import CountryModel from "../models/Country/CountryModel";
import BatchMessageSenderDetail from "../models/BatchMessage/BatchMessageSenderDetailModel";
import BatchMessageSchedulingDetailModel from "../models/BatchMessage/BatchMessageSchedulingDetailModel";
import BatchMessageCostRequestModel from "../models/BatchMessage/BatchMessageCostRequestModel";

import BatchMessagePreviewModel from "../models/BatchMessage/BatchMessagePreviewModel";
import UploadAudioFileRequestModel from "../models/RequestForHelpTemplates/UploadAudioFileRequestModel";
import { ContentTemplateResponse } from "../models/BatchMessage/ContentTemplate";

export async function GetBatchMessagingDetail(
	partitionKey: string,
	rowKey: string
): Promise<ApiResponse<BatchMessageDetailModel>> {
	return apiPlugin<BatchMessageDetailModel>(
		"GET",
		"GetBatchMessageDetailAsync",
		new URLSearchParams({
			partitionKey,
			rowKey,
		})
	);
}

export async function SavePreSubmissionConfigurationDetail(
	model: PreSubmissionConfigurationModel
): Promise<ApiResponse<PreSubmissionConfigurationModel>> {
	return apiPlugin<PreSubmissionConfigurationModel>(
		"POST",
		"SavePreSubmissionConfigurationDetail",
		undefined,
		model
	);
}

export async function GetAwaitingApprovalTemplates(): Promise<
	ApiResponse<PreSubmissionConfigurationModel[]>
> {
	return apiPlugin<PreSubmissionConfigurationModel[]>(
		"GET",
		"GetAwaitingApprovalTemplates"
	);
}

export async function ApproveTemplateAsync(
	rowKey: string,
	partitionKey: string
): Promise<ApiResponse<undefined>> {
	return apiPlugin<undefined>(
		"GET",
		"ApproveTemplateAsync",
		new URLSearchParams({
			partitionKey,
			rowKey,
		}),
		undefined,
		true
	);
}

export async function RejectTemplateAsync(
	rowKey: string,
	partitionKey: string
): Promise<ApiResponse<undefined>> {
	return apiPlugin<undefined>(
		"GET",
		"RejectTemplateAsync",
		new URLSearchParams({
			partitionKey,
			rowKey,
		}),
		undefined,
		true
	);
}

export async function GetMyTemplates(): Promise<
	ApiResponse<PreSubmissionConfigurationModel[]>
> {
	return apiPlugin<PreSubmissionConfigurationModel[]>("GET", "GetMyTemplates");
}

export async function SaveMessageDetail(
	messageDetail: BatchMessageLanguageDetailModel
): Promise<ApiResponse<BatchMessageLanguageDetailModel>> {
	return apiPlugin<BatchMessageLanguageDetailModel>(
		"POST",
		"SaveMessageDetail",
		undefined,
		messageDetail
	);
}

export async function GetMyScheduledTemplates(): Promise<
	ApiResponse<PreSubmissionConfigurationModel[]>
> {
	return apiPlugin<PreSubmissionConfigurationModel[]>(
		"GET",
		"GetMyScheduledTemplates"
	);
}

export async function GetSASTokenForBlobFile(
	fileName: string,
	containerName: string
): Promise<ApiResponse<string>> {
	let headers: Record<string, string> = {};
	// set authorization header

	const token = Shared.getAccessToken();
	if (!token) {
		return {
			error: "Invalid Request",
		};
	}
	// set authorization header
	headers["Authorization"] = "bearer " + token;

	const url: string = `${process.env.REACT_APP_API_BASE_URL}/api/GetSASTokenForBlobFile?fileName=${fileName}&containerName=${containerName}`;
	const res: any = await fetch(url, {
		method: "get",
		headers,
	});
	if (res.ok) {
		try {
			const resData = (await res.text()) as string;
			return {
				responseObj: resData,
			};
		} catch (err) {
			return {
				error: "failed to parse response",
			};
		}
	} else {
		return {
			error: `${res.status} - ${res.statusText || ""}`,
		};
	}
}

export async function SaveBatchMessageDraft(
	model: BatchMessageDraftModel
): Promise<ApiResponse<boolean>> {
	return apiPlugin<boolean>("POST", "SaveBatchMessageDraft", undefined, model);
}

export async function SchedulePreviewBatchMessage(
	model: BatchMessagePreviewModel
): Promise<ApiResponse<boolean>> {
	return apiPlugin<boolean>(
		"POST",
		"SchedulePreviewBatchMessage",
		undefined,
		model
	);
}

// change return type in model
export async function SaveGroupDetail(
	groupDetail: BatchMessageGroupDetailModel
): Promise<ApiResponse<BatchMessageGroupDetailModel>> {
	return apiPlugin<BatchMessageGroupDetailModel>(
		"POST",
		"SaveGroupDetail",
		undefined,
		groupDetail
	);
}

export async function GetCountryByPreSubmissionConfigurationId(
	rowKey: string,
	partitionKey: string
): Promise<ApiResponse<CountryModel>> {
	return apiPlugin<CountryModel>(
		"GET",
		"GetCountryByPreSubmissionConfigurationId",
		new URLSearchParams({
			rowKey,
			partitionKey,
		})
	);
}

export async function GetIncomingPhoneNumbers(
	rowKey: string,
	partitionKey: string
): Promise<ApiResponse<PhoneNumbers[]>> {
	return apiPlugin<PhoneNumbers[]>(
		"GET",
		"GetIncomingPhoneNumbers",
		new URLSearchParams({
			rowKey,
			partitionKey,
		})
	);
}

export async function SaveMessageSenderDetail(
	model: BatchMessageSenderDetail
): Promise<ApiResponse<undefined>> {
	return apiPlugin<undefined>(
		"POST",
		"SaveMessageSenderDetail",
		undefined,
		model
	);
}

export async function SaveBatchMessageScheduleDetail(
	messageDetail: BatchMessageSchedulingDetailModel
): Promise<ApiResponse<BatchMessageSchedulingDetailModel>> {
	return apiPlugin<BatchMessageSchedulingDetailModel>(
		"POST",
		"SaveMessageSchedulingDetail",
		undefined,
		messageDetail
	);
}

export async function GetMessageScheduleCostDetail(
	messageCostRequest: BatchMessageCostRequestModel
): Promise<ApiResponse<BatchMessageCostRequestModel>> {
	return apiPlugin<BatchMessageCostRequestModel>(
		"POST",
		"GetMessageCost",
		undefined,
		messageCostRequest
	);
}

export async function GetPreSubmissionConfigurationAsync(
	partitionKey: string,
	rowKey: string
): Promise<ApiResponse<PreSubmissionConfigurationModel>> {
	return apiPlugin<PreSubmissionConfigurationModel>(
		"GET",
		"GetPreSubmissionConfigurationAsync",
		new URLSearchParams({
			partitionKey,
			rowKey,
		})
	);
}

export async function GetBatchMessagesForTemplates(
	batchMessageDetails: FetchBatchMessageDetailModel[]
): Promise<ApiResponse<BatchMessageSchedulingDetailModel[]>> {
	return apiPlugin<BatchMessageSchedulingDetailModel[]>(
		"POST",
		"GetBatchMessagesForTemplates",
		undefined,
		batchMessageDetails
	);
}

export async function GetLanguagesForIds(
	rowKeys: string[]
): Promise<ApiResponse<LanguageModel[]>> {
	return apiPlugin<LanguageModel[]>(
		"GET",
		"GetLanguageDetailForIds",
		new URLSearchParams(rowKeys.map((key) => ["languageId", key]))
	);
}

export async function CancelRun(
	userId: string,
	partitionKey: string,
	rowKey: string,
	cancelAllRuns: boolean
): Promise<ApiResponse<undefined>> {
	const cancelAll = cancelAllRuns.toString();
	return apiPlugin<undefined>(
		"POST",
		"CancelRun",
		new URLSearchParams({ userId, partitionKey, rowKey, cancelAll }),
		undefined,
		true
	);
}

export async function UploadAudioFilesAsync(
	audioFiles: UploadAudioFileRequestModel
): Promise<ApiResponse<UploadAudioFileRequestModel>> {
	return apiPlugin<UploadAudioFileRequestModel>(
		"POST",
		"UploadAudioFilesAsync",
		undefined,
		audioFiles
	);
}

export async function DeleteAudioFileAsync(
	containerName: string,
	blobName: string,
	partitionKey: string,
	rowKey: string
): Promise<ApiResponse<PreSubmissionConfigurationModel>> {
	return apiPlugin<PreSubmissionConfigurationModel>(
		"GET",
		"DeleteAudioFileAsync",
		new URLSearchParams({
			containerName,
			blobName,
			partitionKey,
			rowKey,
		}),
		undefined,
		true
	);
}

export async function GenerateScript(
	partitionKey: string,
	rowKey: string
): Promise<ApiResponse<string>> {
	let headers: Record<string, string> = {};

	const token = Shared.getAccessToken();

	if (!token) {
		return {
			error: "Invalid Request",
		};
	}
	// set authorization header
	headers["Authorization"] = "bearer " + token;
	const url: string = `${process.env.REACT_APP_API_BASE_URL}/api/GenerateScript?partitionKey=${partitionKey}&rowKey=${rowKey}`;
	const res: any = await fetch(url, {
		method: "get",
		headers,
	});
	if (res.ok) {
		try {
			const resData = (await res.text()) as string;
			return {
				responseObj: resData,
			};
		} catch (err) {
			return {
				error: "failed to parse response",
			};
		}
	} else {
		return {
			error: `${res.status} - ${res.statusText || ""}`,
		};
	}
}

export async function GetContentTemplates(
	rowKey: string
): Promise<ApiResponse<ContentTemplateResponse>> {
	return apiPlugin<ContentTemplateResponse>(
		"GET",
		"GetContentTemplates",
		new URLSearchParams({
			rowKey,
		})
	);
}
